import React, { useEffect, useState, useRef, useCallback } from "react";
import { auth, db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity } from "../context/EffectiveIdentityContext";
import { formatCurrencyUY } from "../utils/formatters";
import { filterPaymentsVisibleToResidents } from "../utils/cierrePublicacion";
import { tolerantId } from "../utils/firestoreHelpers";
import { leerGastosPorId, leerPagosParaResidente } from "../utils/consultasEdificio";
import { leerFichaResidente } from "../utils/fichaResidente";
import {
  FaFileInvoiceDollar,
  FaCheckCircle,
  FaChartBar,
  FaCalendarAlt,
  FaWallet,
  FaBalanceScale,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaShieldAlt,
  FaGavel,
  FaPercentage,
  FaLayerGroup,
  FaSwimmingPool,
  FaSpinner,
  FaExclamationTriangle,
  FaHome,
} from "react-icons/fa";
import { friendlyBreakdownName } from "../utils/fundHelpers";
import { useIsMobile } from "../hooks/useResponsive";
import {
  AppScreen,
  AppHeader,
  AppCard,
  StatCard,
  SectionLabel,
  AppButton,
  Money,
  SegmentedControl,
} from "../components/app";
import { appColors, appSpacing } from "../utils/appTheme";
import BuildingAccountView from "../components/BuildingAccountView";
import PaymentEvolutionChart from "../components/PaymentEvolutionChart";
import { useTranslation } from "react-i18next";

// Colores distintos por categoría de fondo (todos diferenciables)
const FUND_COLORS = {
  gastos_comunes: "#0080ff",
  fondo_reserva: "#8b5cf6",
  multas: "#ef4444",
  intereses: "#f59e0b",
  amenities: "#10b981",
  cargos: "#d97706",
  alquiler: "#0066cc",
  custom: "#e67e22",
};

// Calcular breakdown por fondo de un conjunto de payments (exportado para tests)
export const computeBreakdown = (payments, parseAmount) => {
  let gastosComunes = 0, fondoReserva = 0, multasTotal = 0, interesesTotal = 0, amenitiesTotal = 0, cargosTotal = 0, reajusteTotal = 0, alquilerTotal = 0;
  const customFundTotals = {};

  payments.forEach(p => {
    const amount = parseAmount(p.amount);
    const fr = parseAmount(p.fondoReservaAmount);
    const mt = parseAmount(p.multaAmount);
    let int = 0;
    let rj = 0;
    if (p.interestBreakdown && Array.isArray(p.interestBreakdown)) {
      int = p.interestBreakdown.reduce((s, ib) => s + (parseAmount(ib.interest) || parseAmount(ib.amount) || 0), 0);
      rj = p.interestBreakdown.reduce((s, ib) => s + (parseAmount(ib.reajuste) || 0), 0);
    }
    // Compat: reajuste también puede venir como línea del breakdown.
    if (rj === 0 && p.breakdown?.reajuste != null) rj = parseAmount(p.breakdown.reajuste);
    // Alquiler de una unidad del edificio: va aparte, no es gasto común.
    const alq = parseAmount(p.breakdown?.alquiler) || 0;
    alquilerTotal += alq;
    fondoReserva += fr;
    multasTotal += mt;
    interesesTotal += int;
    reajusteTotal += rj;

    if (p.breakdown) {
      Object.entries(p.breakdown).forEach(([key, val]) => {
        if (key.startsWith("fondo_") && key !== "fondo_reserva") {
          customFundTotals[key] = (customFundTotals[key] || 0) + parseAmount(val);
        }
        if (key.startsWith("amenity__")) {
          amenitiesTotal += parseAmount(val);
        }
        if (key.startsWith("cargo__")) {
          cargosTotal += parseAmount(val);
        }
      });
    }

    gastosComunes += p.breakdown?.gastos_comunes != null
      ? parseAmount(p.breakdown.gastos_comunes)
      : Math.max(0, amount - fr - mt - int - rj - alq);
  });

  const round = (v) => Math.round(v * 100) / 100;
  return {
    gastos_comunes: round(gastosComunes),
    fondo_reserva: round(fondoReserva),
    multas: round(multasTotal),
    intereses: round(interesesTotal),
    reajuste: round(reajusteTotal),
    amenities: round(amenitiesTotal),
    cargos: round(cargosTotal),
    alquiler: round(alquilerTotal),
    ...Object.fromEntries(
      Object.entries(customFundTotals).map(([k, v]) => [k, round(v)])
    ),
  };
};

const Cuentas = ({ setSelected, goBack }) => {
  const { t } = useTranslation("cuentas");
  const monthsShort = t("monthsShort", { returnObjects: true });
  const isMobile = useIsMobile();
  const { selectedBuilding, selectedUnit, userUnitsInBuilding, soloAlquilerEdificio } = useBuilding();
  const { email: effEmail, userid: effUserid, readOnly: viendoComo } = useEffectiveIdentity();
  const [totalDeudaMes, setTotalDeudaMes] = useState(0);
  const [totalPagadoMes, setTotalPagadoMes] = useState(0);
  const [annualSummary, setAnnualSummary] = useState({
    totalPagado: 0,
    totalDeuda: 0,
    promedioMensual: 0,
    mesesPagados: 0
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [allPayments, setAllPayments] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState({});
  // Mes seleccionado para el panel de "Composición" (desktop). Lo reporta el
  // gráfico de evolución al hacer click en una barra. Default: mes actual.
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const d = new Date();
    return { month: d.getMonth(), year: d.getFullYear() };
  });
  const [gastosSubBreakdown, setGastosSubBreakdown] = useState({});
  const [loadingGastosDetalle, setLoadingGastosDetalle] = useState(false);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 768);
  // Toggle "Mi cuenta / El edificio" (solo visible para propietarios).
  const [accountView, setAccountView] = useState("mia");
  const gastosCache = useRef({});

  // La vista "El edificio" (BuildingAccountView: pagos de todas las unidades)
  // es para propietarios VIGENTES: owner/ownerSecondary de una unidad no
  // histórica en un edificio que no es de solo lectura. El ex-propietario lee
  // solo sus pagos (reglas 0D), así que no se monta.
  const isOwner =
    !selectedBuilding?.isReadOnly &&
    (userUnitsInBuilding || []).some((u) => u.userRole === "Propietario" && !u.isHistorical);
  const accountViewOptions = [
    { value: "mia", label: t("accountView.mine") },
    { value: "edificio", label: t("accountView.building") },
  ];

  // Responsivo
  useEffect(() => {
    const handler = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Helpers
  const parseAmount = (val) => {
    if (val == null) return 0;
    if (typeof val === "number") return isNaN(val) ? 0 : val;
    const cleaned = String(val).replace(/\s/g, "").replace(/[^\d,.-]/g, "");
    if (!cleaned || cleaned === "-") return 0;

    const commaCount = (cleaned.match(/,/g) || []).length;
    const dotCount = (cleaned.match(/\./g) || []).length;

    if (commaCount > 0 && dotCount > 0) {
      const lastComma = cleaned.lastIndexOf(",");
      const lastDot = cleaned.lastIndexOf(".");
      if (lastComma > lastDot) {
        return Number(cleaned.replace(/\./g, "").replace(",", ".")) || 0;
      } else {
        return Number(cleaned.replace(/,/g, "")) || 0;
      }
    }

    if (commaCount > 0) {
      if (commaCount > 1) {
        return Number(cleaned.replace(/,/g, "")) || 0;
      }
      const parts = cleaned.split(",");
      if (parts[1] && parts[1].length === 3 && /^\d{1,3}$/.test(parts[0].replace("-", ""))) {
        return Number(cleaned.replace(",", "")) || 0;
      }
      return Number(cleaned.replace(",", ".")) || 0;
    }

    // BUG-44 fix: En Uruguay, dot es separador de miles
    if (dotCount > 1) {
      return Number(cleaned.replace(/\./g, "")) || 0;
    }
    if (dotCount === 1) {
      const parts = cleaned.split(".");
      if (parts[1] && parts[1].length === 3) {
        return Number(cleaned.replace(".", "")) || 0;
      }
    }

    return Number(cleaned) || 0;
  };

  const toDate = (val) => {
    if (!val) return null;
    if (val?.seconds) return new Date(val.seconds * 1000);
    if (val instanceof Date) return val;
    const s = String(val).trim();
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) return new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
    const dt = new Date(s);
    return isNaN(dt.getTime()) ? null : dt;
  };

  const isSameMonthYear = (d, base = new Date()) => {
    if (!d) return false;
    return d.getMonth() === base.getMonth() && d.getFullYear() === base.getFullYear();
  };

  const fmtCurrency = formatCurrencyUY;

  // ============================================================
  // Fetch principal de pagos
  // ============================================================
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        if (!selectedBuilding?.buildingid) return;
        if (!effEmail) return;

        // Ficha del residente (en "ver como", del contexto: quien mira es staff
        // y con C10 no lista users por email).
        const ficha = await leerFichaResidente(db, { email: effEmail, userid: effUserid, viendoComo });
        const userId = ficha ? ficha.userid : auth.currentUser?.uid;
        const normalizedUserId = String(userId);

        const apartmentsQ = query(
          collection(db, "apartments"),
          where("building", "in", tolerantId(selectedBuilding.buildingid))
        );
        const apartmentsSnap = await getDocs(apartmentsQ);
        const apartments = apartmentsSnap.docs.map((d) => d.data());
        const visiblePaymentUserIds = new Set([normalizedUserId]);
        apartments.forEach((apt) => {
          if (String(apt.ownerSecondary) === normalizedUserId && apt.owner != null && String(apt.owner).trim() !== "") {
            visiblePaymentUserIds.add(String(apt.owner));
          }
          if (String(apt.tenantSecondary) === normalizedUserId && apt.tenant != null && String(apt.tenant).trim() !== "") {
            visiblePaymentUserIds.add(String(apt.tenant));
          }
        });

        // El ex-ocupante solo lee sus pagos (reglas 0D): si la consulta por
        // edificio se niega, leerPagosParaResidente la repite con sus userid.
        // En "ver como" no hay segundo intento (el que mira es staff).
        const allBuildingPayments = await leerPagosParaResidente(db, selectedBuilding.buildingid, {
          uid: viendoComo ? null : auth.currentUser?.uid || null,
        });
        const ownPayments = allBuildingPayments.filter(
          (p) => String(p.userid) === normalizedUserId
        );
        let data = filterPaymentsVisibleToResidents(
          ownPayments.length > 0
            ? ownPayments
            : allBuildingPayments.filter((p) => visiblePaymentUserIds.has(String(p.userid)))
        );
        if (selectedUnit) {
          // Bloque B del fix multi-unidad: los payments nuevos (post-cierre
          // con el fix de CierreMes) traen `apartmentId` poblado. Filtramos
          // primero por ese campo — más exacto que la heurística por userid.
          //
          // Para payments legacy generados ANTES del fix (sin apartmentId):
          // si no hay ningún payment con apartmentId en este lote, caemos al
          // matching heurístico previo. Si hay payments con apartmentId
          // mezclados con legacy, los legacy se ignoran para esa unidad para
          // evitar duplicación (el operador debe re-generar el cierre viejo
          // si quiere ver esos legacy correctamente).
          const hasAnyApartmentId = data.some((p) => p.apartmentId);
          data = data.filter((p) => {
            if (p.apartmentId) {
              return p.apartmentId === selectedUnit.id;
            }
            if (p.unitId) return p.unitId === selectedUnit.id;
            if (hasAnyApartmentId) {
              // Hay payments con apartmentId en este lote: los legacy
              // (sin apartmentId) se filtran para no duplicar el monto.
              return false;
            }
            // Lote 100% legacy: fallback al matching por userid del owner/tenant.
            const uid = String(p.userid);
            return (
              uid === String(selectedUnit.owner) ||
              uid === String(selectedUnit.tenant) ||
              uid === `unit_${selectedUnit.id}`
            );
          });
        }

        let deuda = 0;
        let pagado = 0;
        const now = new Date();
        const currentYear = now.getFullYear();

        for (const p of data) {
          const maturity = toDate(p.maturity);
          const amount = parseAmount(p.amount);
          if (isSameMonthYear(maturity, now)) {
            if (p.paid) pagado += amount;
            else deuda += amount;
          }
        }

        setTotalDeudaMes(deuda);
        setTotalPagadoMes(pagado);
        setAllPayments(data);

        // Breakdown del mes actual para las tarjetas de desglose
        const currentMonth = now.getMonth() + 1;
        const currentMonthPayments = data.filter(p => p.month === currentMonth && p.year === currentYear);
        setCategoryBreakdown(computeBreakdown(currentMonthPayments, parseAmount));

        // Años disponibles
        const yearsSet = new Set();
        yearsSet.add(currentYear);
        for (const p of data) {
          const maturity = toDate(p.maturity);
          if (maturity) yearsSet.add(maturity.getFullYear());
        }
        setAvailableYears(Array.from(yearsSet).sort((a, b) => b - a));

        // Limpiar cache de gastos sub-breakdown al cambiar edificio/unidad
        gastosCache.current = {};

      } catch (error) {
        console.error("Error al traer pagos:", error);
      }
    };

    fetchPayments();
  }, [selectedBuilding, selectedUnit?.id, effEmail, effUserid, viendoComo]);

  // ============================================================
  // Recalcular resumen anual
  // ============================================================
  useEffect(() => {
    const yearData = allPayments.filter(p => {
      const maturity = toDate(p.maturity);
      return maturity && maturity.getFullYear() === selectedYear;
    });

    const totalPagadoAnual = yearData
      .filter(p => p.paid)
      .reduce((sum, p) => sum + parseAmount(p.amount), 0);

    const totalDeudaAnual = yearData
      .filter(p => !p.paid)
      .reduce((sum, p) => sum + parseAmount(p.amount), 0);

    const mesesConPagos = new Set(
      yearData
        .filter(p => p.paid)
        .map(p => toDate(p.maturity)?.getMonth())
        .filter(m => m !== undefined)
    ).size;

    setAnnualSummary({
      totalPagado: totalPagadoAnual,
      totalDeuda: totalDeudaAnual,
      promedioMensual: mesesConPagos > 0 ? totalPagadoAnual / mesesConPagos : 0,
      mesesPagados: mesesConPagos
    });
  }, [selectedYear, allPayments]);

  // ============================================================
  // Fetch detalle gastos comunes por tipo de proveedor
  // ============================================================
  const fetchGastosSubBreakdown = useCallback(async (monthNum, yearNum) => {
    if (!selectedBuilding?.buildingid) return;
    const cacheKey = `${monthNum}-${yearNum}`;
    if (gastosCache.current[cacheKey]) {
      setGastosSubBreakdown(gastosCache.current[cacheKey]);
      return;
    }

    setLoadingGastosDetalle(true);
    try {
      // 1. Buscar expensesapartments del mes
      const eaQ = query(
        collection(db, "expensesapartments"),
        where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
        where("month", "==", monthNum),
        where("year", "==", yearNum)
      );
      const eaSnap = await getDocs(eaQ);
      let eaDocs = eaSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Filtrar por unidad del usuario si hay selectedUnit
      if (selectedUnit) {
        eaDocs = eaDocs.filter(ea => ea.apartment === selectedUnit.id);
      }

      if (eaDocs.length === 0) {
        setGastosSubBreakdown({});
        gastosCache.current[cacheKey] = {};
        return;
      }

      // 2. Obtener gastos únicos, por id con getDoc (una consulta por __name__
      // no fija el edificio y las reglas 0D la niegan entera).
      const expenseIds = [...new Set(eaDocs.map(ea => ea.expenseId).filter(Boolean))];
      let expenseDocs = [];
      try {
        expenseDocs = await leerGastosPorId(db, expenseIds);
      } catch (e) {
        console.error("Error cargando gastos del mes:", e);
      }

      // 3. Filtrar solo gastos comunes
      const gastosComunes = expenseDocs.filter(e =>
        !e.expenseCategory || e.expenseCategory === "gastos_comunes"
      );

      // 4. Obtener proveedores únicos
      const supplierIds = [...new Set(gastosComunes.map(e => e.supplierid).filter(Boolean))];
      const suppliersMap = {};
      for (let i = 0; i < supplierIds.length; i += 30) {
        const batch = supplierIds.slice(i, i + 30);
        try {
          const supSnap = await getDocs(
            query(collection(db, "supplier"), where("__name__", "in", batch))
          );
          supSnap.docs.forEach(d => { suppliersMap[d.id] = d.data(); });
        } catch (e) {
          console.error("Error cargando lote de proveedores:", e);
        }
      }

      // 5. Agrupar montos por tipo de proveedor
      // Mapear expenseId → monto asignado al usuario
      const expenseAmountMap = {};
      eaDocs.forEach(ea => {
        if (ea.expenseId) {
          expenseAmountMap[ea.expenseId] = (expenseAmountMap[ea.expenseId] || 0) + parseAmount(ea.amount);
        }
      });

      const result = {};
      gastosComunes.forEach(exp => {
        const supplier = suppliersMap[exp.supplierid];
        const category = supplier?.type || supplier?.name || "Otros";
        const userAmount = expenseAmountMap[exp.id] || 0;
        if (userAmount > 0) {
          result[category] = (result[category] || 0) + userAmount;
        }
      });

      // Redondear
      Object.keys(result).forEach(k => { result[k] = Math.round(result[k] * 100) / 100; });

      setGastosSubBreakdown(result);
      gastosCache.current[cacheKey] = result;
    } catch (e) {
      console.error("Error cargando detalle de gastos comunes:", e);
      setGastosSubBreakdown({});
    } finally {
      setLoadingGastosDetalle(false);
    }
  }, [selectedBuilding, selectedUnit]);

  // Trigger fetch del detalle de gastos comunes al cambiar el mes seleccionado.
  useEffect(() => {
    fetchGastosSubBreakdown(selectedPeriod.month + 1, selectedPeriod.year);
  }, [selectedPeriod, fetchGastosSubBreakdown]);

  // ============================================================
  // Valores derivados para el panel de "Composición" (mes seleccionado)
  // ============================================================
  const periodOf = (p) => {
    const d = toDate(p.maturity);
    if (d) return { m: d.getMonth(), y: d.getFullYear() };
    const mm = Number(p.month);
    const yy = Number(p.year);
    if (mm && yy) return { m: mm - 1, y: yy };
    return null;
  };
  const selectedMonthPayments = allPayments.filter((p) => {
    const per = periodOf(p);
    return per && per.m === selectedPeriod.month && per.y === selectedPeriod.year;
  });
  const selectedBreakdown = computeBreakdown(selectedMonthPayments, parseAmount);

  // Preparar items del breakdown para el panel derecho (excluir ceros)
  const breakdownItems = [];
  if (selectedBreakdown.alquiler > 0) {
    breakdownItems.push({ key: "alquiler", label: t("categories.rent"), value: selectedBreakdown.alquiler, color: FUND_COLORS.alquiler });
  }
  if (selectedBreakdown.gastos_comunes > 0) {
    breakdownItems.push({ key: "gastos_comunes", label: t("categories.commonCharges"), value: selectedBreakdown.gastos_comunes, color: FUND_COLORS.gastos_comunes });
  }
  if (selectedBreakdown.fondo_reserva > 0) {
    breakdownItems.push({ key: "fondo_reserva", label: t("categories.reserveFund"), value: selectedBreakdown.fondo_reserva, color: FUND_COLORS.fondo_reserva });
  }
  // Fondos personalizados
  Object.entries(selectedBreakdown)
    .filter(([k, v]) => k.startsWith("fondo_") && k !== "fondo_reserva" && v > 0)
    .forEach(([k, v]) => {
      breakdownItems.push({ key: k, label: friendlyBreakdownName(k, selectedBuilding), value: v, color: FUND_COLORS.custom });
    });
  if (selectedBreakdown.amenities > 0) {
    breakdownItems.push({ key: "amenities", label: t("categories.amenities"), value: selectedBreakdown.amenities, color: FUND_COLORS.amenities });
  }
  if (selectedBreakdown.cargos > 0) {
    breakdownItems.push({ key: "cargos", label: t("categories.charges"), value: selectedBreakdown.cargos, color: FUND_COLORS.cargos });
  }
  if (selectedBreakdown.multas > 0) {
    breakdownItems.push({ key: "multas", label: t("categories.fines"), value: selectedBreakdown.multas, color: FUND_COLORS.multas });
  }
  if (selectedBreakdown.reajuste > 0) {
    breakdownItems.push({ key: "reajuste", label: t("categories.adjustment"), value: selectedBreakdown.reajuste, color: FUND_COLORS.intereses });
  }
  if (selectedBreakdown.intereses > 0) {
    breakdownItems.push({ key: "intereses", label: t("categories.lateFee"), value: selectedBreakdown.intereses, color: FUND_COLORS.intereses });
  }
  const breakdownTotal = breakdownItems.reduce((s, i) => s + i.value, 0);

  // ============================================================
  // Estilos inline para categoría
  // ============================================================
  const categoryCardStyle = {
    background: "#fff",
    borderRadius: 8,
    padding: "10px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };
  const categoryLabelStyle = { fontSize: 11, color: "#6b7280", fontWeight: 400 };
  const categoryValueStyle = { fontSize: 16, fontWeight: 400, color: "#1f2937" };

  // ─── Versión mobile: estilo app "Radial" (screen 04) ─────────────────────────
  if (isMobile) {
    // Derivaciones SOLO de presentación (sin estado ni queries nuevas):
    // días en mora = del pago pendiente vencido más antiguo.
    let diasMora = 0;
    const nowM = new Date();
    allPayments.forEach((p) => {
      if (p.paid) return;
      const mat = toDate(p.maturity);
      if (!mat) return;
      const diff = Math.floor((nowM - mat) / (1000 * 60 * 60 * 24));
      if (diff > diasMora) diasMora = diff;
    });

    const backTo = goBack || (setSelected ? () => setSelected("finanzas") : undefined);

    return (
      <AppScreen
        header={
          <AppHeader
            title={t("mobile.title")}
            subtitle={
              selectedBuilding?.name
                ? t("mobile.subtitleMine", { name: selectedBuilding.name })
                : t("mobile.subtitleFallback")
            }
            onBack={backTo}
          />
        }
      >
        {isOwner && (
          <SegmentedControl
            options={accountViewOptions}
            value={accountView}
            onChange={setAccountView}
            style={{ marginBottom: appSpacing.section }}
          />
        )}
        {isOwner && accountView === "edificio" ? (
          <BuildingAccountView buildingId={selectedBuilding?.buildingid} />
        ) : (
        <>
        {/* Tarjetas del mes actual: DEUDA PENDIENTE / PAGADO ESTE MES */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: appSpacing.gridGap,
            marginBottom: appSpacing.section,
          }}
        >
          <StatCard
            icon={<FaExclamationTriangle />}
            label={t("cards.pendingDebt")}
            value={<Money value={totalDeudaMes} size={22} tone={totalDeudaMes > 0 ? "red" : undefined} />}
            valueColor={totalDeudaMes > 0 ? appColors.red : appColors.ink}
            sub={
              totalDeudaMes > 0
                ? diasMora > 0
                  ? t("cards.overdueDays", { count: diasMora })
                  : t("cards.duesThisMonth")
                : t("cards.noDebt")
            }
            subColor={totalDeudaMes > 0 ? appColors.red : appColors.ink2}
            style={{ minWidth: 0 }}
          />
          <StatCard
            icon={<FaCheckCircle />}
            label={t("cards.paidThisMonth")}
            value={<Money value={totalPagadoMes} size={22} tone={totalPagadoMes > 0 ? "green" : undefined} />}
            valueColor={totalPagadoMes > 0 ? appColors.green : appColors.ink}
            sub={totalPagadoMes > 0 ? t("cards.upToDate") : t("cards.noPayments")}
            style={{ minWidth: 0 }}
          />
        </div>

        {/* Evolución de pagos: barra apilada azul/rojo + selector 6/12/18 meses */}
        <AppCard style={{ marginBottom: appSpacing.section }} padding={18}>
          <PaymentEvolutionChart payments={allPayments} parseAmount={parseAmount} initialMonths={6} />
        </AppCard>

        {/* Resumen anual: dos cajas frías (cream = frío) TOTAL PAGADO / DEUDA ACUMULADA */}
        <SectionLabel style={{ marginBottom: 12 }}>{t("annual.summaryYear", { year: selectedYear })}</SectionLabel>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: appSpacing.gridGap,
            marginBottom: totalDeudaMes > 0 && setSelected ? appSpacing.section : 0,
          }}
        >
          <StatCard
            variant="cream"
            icon={<FaCheckCircle />}
            label={t("annual.totalPaidYear", { year: selectedYear })}
            value={<Money value={annualSummary.totalPagado} size={18} tone={annualSummary.totalPagado > 0 ? "green" : undefined} />}
            valueColor={annualSummary.totalPagado > 0 ? appColors.green : appColors.ink}
            style={{ minWidth: 0 }}
          />
          <StatCard
            variant="cream"
            icon={<FaFileInvoiceDollar />}
            label={t("annual.accumulatedDebt")}
            value={<Money value={annualSummary.totalDeuda} size={18} tone={annualSummary.totalDeuda > 0 ? "red" : undefined} />}
            valueColor={annualSummary.totalDeuda > 0 ? appColors.red : appColors.ink}
            style={{ minWidth: 0 }}
          />
        </div>

        {/* Pagar deuda pendiente: botón primario full-width al pie (como la app) */}
        {totalDeudaMes > 0 && setSelected && (
          <AppButton block onClick={() => setSelected("expensas")}>
            {t("pay", { amount: fmtCurrency(totalDeudaMes) })}
          </AppButton>
        )}
        </>
        )}
      </AppScreen>
    );
  }

  // ============================================================
  // Render (desktop)
  // ============================================================
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <FaWallet />
        </div>
        <div style={styles.headerInfo}>
          <h1 style={styles.title}>{t("header.title")}</h1>
          <p style={styles.subtitle}>{t("header.subtitle")}</p>
        </div>
      </div>

      {isOwner && (
        <div style={{ maxWidth: 300 }}>
          <SegmentedControl
            options={accountViewOptions}
            value={accountView}
            onChange={setAccountView}
          />
        </div>
      )}

      {isOwner && accountView === "edificio" ? (
        <BuildingAccountView buildingId={selectedBuilding?.buildingid} />
      ) : (
      <>
      {/* Tarjetas del mes actual */}
      <div style={styles.cardsRow}>
        <div style={styles.card}>
          <div style={{ ...styles.iconWrapper, backgroundColor: "#ef4444" }}>
            <FaFileInvoiceDollar size={22} color="white" />
          </div>
          <div>
            <h3 style={styles.cardTitle}><FaClock style={{ marginRight: 4, fontSize: 12 }} /> {t("cards.pendingDebt")}</h3>
            <p style={{ ...styles.amount, color: totalDeudaMes > 0 ? "#ef4444" : "#1a1a1a" }}>
              {fmtCurrency(totalDeudaMes)}
            </p>
          </div>
        </div>
        <div style={styles.card}>
          <div style={{ ...styles.iconWrapper, backgroundColor: "#22c55e" }}>
            <FaCheckCircle size={22} color="white" />
          </div>
          <div>
            <h3 style={styles.cardTitle}><FaCheckCircle style={{ marginRight: 4, fontSize: 12 }} /> {t("cards.paidThisMonth")}</h3>
            <p style={{ ...styles.amount, color: "#22c55e" }}>
              {fmtCurrency(totalPagadoMes)}
            </p>
          </div>
        </div>
      </div>

      {/* Desglose por Categoría */}
      {(categoryBreakdown.fondo_reserva > 0 || categoryBreakdown.multas > 0 || categoryBreakdown.cargos > 0 ||
        categoryBreakdown.intereses > 0 || categoryBreakdown.reajuste > 0 || categoryBreakdown.amenities > 0 ||
        categoryBreakdown.alquiler > 0 ||
        Object.keys(categoryBreakdown).some(k => k.startsWith("fondo_") && k !== "fondo_reserva" && categoryBreakdown[k] > 0)
      ) && (
        <div style={{ marginBottom: 0 }}>
          <h3 style={{ fontSize: 14, fontWeight: 400, color: "#1f2937", marginBottom: 8, fontFamily: "'FT Habit', sans-serif" }}>
            {t("categories.title")}
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8 }}>
            {categoryBreakdown.alquiler > 0 && (
              <div style={categoryCardStyle}>
                <FaHome size={16} color={FUND_COLORS.alquiler} />
                <span style={categoryLabelStyle}>{t("categories.rent")}</span>
                <span style={{ ...categoryValueStyle, color: FUND_COLORS.alquiler }}>{formatCurrencyUY(categoryBreakdown.alquiler)}</span>
              </div>
            )}
            {/* El inquilino de una unidad del edificio no paga gastos comunes ni fondo: sin tarjetas en $0. */}
            {!(soloAlquilerEdificio && !(categoryBreakdown.gastos_comunes > 0)) && (
              <div style={categoryCardStyle}>
                <FaWallet size={16} color="#0080ff" />
                <span style={categoryLabelStyle}>{t("categories.commonCharges")}</span>
                <span style={categoryValueStyle}>{formatCurrencyUY(categoryBreakdown.gastos_comunes)}</span>
              </div>
            )}
            {!(soloAlquilerEdificio && !(categoryBreakdown.fondo_reserva > 0)) && (
              <div style={categoryCardStyle}>
                <FaShieldAlt size={16} color="#8b5cf6" />
                <span style={categoryLabelStyle}>{t("categories.reserveFund")}</span>
                <span style={categoryValueStyle}>{formatCurrencyUY(categoryBreakdown.fondo_reserva)}</span>
              </div>
            )}
            {/* Fondos personalizados (FIN-019) */}
            {Object.entries(categoryBreakdown)
              .filter(([key, val]) => key.startsWith("fondo_") && key !== "fondo_reserva" && val > 0)
              .map(([key, val]) => (
                <div key={key} style={{...categoryCardStyle, borderColor: "#e5e7eb"}}>
                  <FaLayerGroup size={16} color="#e67e22" />
                  <span style={categoryLabelStyle}>{friendlyBreakdownName(key, selectedBuilding)}</span>
                  <span style={categoryValueStyle}>{formatCurrencyUY(val)}</span>
                </div>
              ))
            }
            {categoryBreakdown.amenities > 0 && (
              <div style={{...categoryCardStyle, borderColor: "#d1fae5"}}>
                <FaSwimmingPool size={16} color="#10b981" />
                <span style={categoryLabelStyle}>{t("categories.amenities")}</span>
                <span style={{...categoryValueStyle, color: "#10b981"}}>{formatCurrencyUY(categoryBreakdown.amenities)}</span>
              </div>
            )}
            {categoryBreakdown.multas > 0 && (
              <div style={{...categoryCardStyle, borderColor: "#fecaca"}}>
                <FaGavel size={16} color="#ef4444" />
                <span style={categoryLabelStyle}>{t("categories.fines")}</span>
                <span style={{...categoryValueStyle, color: "#ef4444"}}>{formatCurrencyUY(categoryBreakdown.multas)}</span>
              </div>
            )}
            {categoryBreakdown.cargos > 0 && (
              <div style={{...categoryCardStyle, borderColor: "#fde68a"}}>
                <FaFileInvoiceDollar size={16} color={FUND_COLORS.cargos} />
                <span style={categoryLabelStyle}>{t("categories.charges")}</span>
                <span style={{...categoryValueStyle, color: FUND_COLORS.cargos}}>{formatCurrencyUY(categoryBreakdown.cargos)}</span>
              </div>
            )}
            {categoryBreakdown.reajuste > 0 && (
              <div style={{...categoryCardStyle, borderColor: "#fed7aa"}}>
                <FaPercentage size={16} color="#f59e0b" />
                <span style={categoryLabelStyle}>{t("categories.adjustment")}</span>
                <span style={{...categoryValueStyle, color: "#f59e0b"}}>{formatCurrencyUY(categoryBreakdown.reajuste)}</span>
              </div>
            )}
            {categoryBreakdown.intereses > 0 && (
              <div style={{...categoryCardStyle, borderColor: "#fed7aa"}}>
                <FaPercentage size={16} color="#f59e0b" />
                <span style={categoryLabelStyle}>{t("categories.lateFee")}</span>
                <span style={{...categoryValueStyle, color: "#f59e0b"}}>{formatCurrencyUY(categoryBreakdown.intereses)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ EVOLUCIÓN DE PAGOS + DESGLOSE ============ */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionIconWrapper}>
            <FaChartBar />
          </div>
          <h2 style={styles.sectionTitle}>{t("evolution.title")}</h2>
        </div>

        <div style={{ display: "flex", flexDirection: isNarrow ? "column" : "row", gap: 0 }}>
          {/* ---- GRÁFICO (78%): barra apilada azul/rojo + selector 6/12/18 ---- */}
          <div style={{
            flex: isNarrow ? "1 1 auto" : "0 0 78%",
            minWidth: 0,
            paddingRight: isNarrow ? 0 : 16,
            marginBottom: isNarrow ? 12 : 0,
          }}>
            <PaymentEvolutionChart
              payments={allPayments}
              parseAmount={parseAmount}
              initialMonths={6}
              title={null}
              selectable
              onSelectMonth={(item) => setSelectedPeriod({ month: item.month, year: item.year })}
            />
          </div>

          {/* ---- COMPOSICIÓN (20%) ---- */}
          <div style={{
            flex: 1,
            minWidth: 0,
            borderLeft: isNarrow ? "none" : "1px solid #f0f0f0",
            paddingLeft: isNarrow ? 0 : 14,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {t("evolution.composition")}
              </span>
              <span style={{ fontSize: 10, color: "#b0b5bc", marginLeft: 6 }}>
                {monthsShort[selectedPeriod.month]} {selectedPeriod.year}
              </span>
            </div>

            {breakdownItems.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {breakdownItems.map(item => {
                  const pct = breakdownTotal > 0 ? (item.value / breakdownTotal) * 100 : 0;
                  return (
                    <div key={item.key}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                        <span style={{ fontSize: 10, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.label}
                        </span>
                        <span style={{ fontSize: 10, color: "#374151", whiteSpace: "nowrap", marginLeft: 4 }}>
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                      <div style={{ height: 4, backgroundColor: "#f3f4f6", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{
                          height: "100%",
                          width: `${pct}%`,
                          backgroundColor: item.color,
                          borderRadius: 2,
                          transition: "width 0.25s ease",
                        }} />
                      </div>
                    </div>
                  );
                })}
                {/* Total */}
                <div style={{ borderTop: "1px solid #f0f0f0", marginTop: 2, paddingTop: 4, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 10, color: "#374151" }}>{t("evolution.total")}</span>
                  <span style={{ fontSize: 10, color: "#374151" }}>{fmtCurrency(breakdownTotal)}</span>
                </div>
              </div>
            ) : (
              <span style={{ fontSize: 10, color: "#c4c8cc" }}>{t("evolution.noData")}</span>
            )}

            {/* Detalle Gastos Comunes */}
            {(Object.keys(gastosSubBreakdown).length > 0 || loadingGastosDetalle) && (
              <>
                <div style={{ height: 1, backgroundColor: "#f0f0f0", margin: "8px 0 6px" }} />
                <span style={{ fontSize: 9, color: "#b0b5bc", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4, display: "block" }}>
                  {t("evolution.commonChargesDetail")}
                </span>
                {loadingGastosDetalle ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <FaSpinner className="spin" size={9} color="#c4c8cc" />
                    <span style={{ fontSize: 10, color: "#c4c8cc" }}>...</span>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {Object.entries(gastosSubBreakdown)
                      .sort(([, a], [, b]) => b - a)
                      .map(([category, amount]) => (
                        <div key={category} style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 10, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{category}</span>
                          <span style={{ fontSize: 10, color: "#374151", whiteSpace: "nowrap", marginLeft: 4 }}>{fmtCurrency(amount)}</span>
                        </div>
                      ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Resumen Anual */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionIconWrapper}>
            <FaCalendarAlt />
          </div>
          <h2 style={styles.sectionTitle}>{t("annual.title", { year: selectedYear })}</h2>
          <div style={styles.yearNav}>
            <button
              style={{
                ...styles.yearNavBtn,
                opacity: availableYears.indexOf(selectedYear) < availableYears.length - 1 ? 1 : 0.3,
              }}
              onClick={() => {
                const idx = availableYears.indexOf(selectedYear);
                if (idx < availableYears.length - 1) setSelectedYear(availableYears[idx + 1]);
              }}
              disabled={availableYears.indexOf(selectedYear) >= availableYears.length - 1}
              title={t("annual.prevYear")}
            >
              <FaChevronLeft size={12} />
            </button>
            <span style={styles.yearLabel}>{selectedYear}</span>
            <button
              style={{
                ...styles.yearNavBtn,
                opacity: availableYears.indexOf(selectedYear) > 0 ? 1 : 0.3,
              }}
              onClick={() => {
                const idx = availableYears.indexOf(selectedYear);
                if (idx > 0) setSelectedYear(availableYears[idx - 1]);
              }}
              disabled={availableYears.indexOf(selectedYear) <= 0}
              title={t("annual.nextYear")}
            >
              <FaChevronRight size={12} />
            </button>
          </div>
        </div>

        <div style={styles.annualGrid}>
          <div style={styles.annualCard}>
            <div style={styles.annualIconWrapper}>
              <FaCheckCircle color="#22c55e" size={20} />
            </div>
            <div style={styles.annualInfo}>
              <span style={styles.annualLabel}>{t("annual.totalPaid")}</span>
              <span style={{ ...styles.annualValue, color: "#22c55e" }}>
                {fmtCurrency(annualSummary.totalPagado)}
              </span>
            </div>
          </div>

          <div style={styles.annualCard}>
            <div style={styles.annualIconWrapper}>
              <FaFileInvoiceDollar color="#ef4444" size={20} />
            </div>
            <div style={styles.annualInfo}>
              <span style={styles.annualLabel}>{t("annual.accumulatedDebtLong")}</span>
              <span style={{ ...styles.annualValue, color: "#ef4444" }}>
                {fmtCurrency(annualSummary.totalDeuda)}
              </span>
            </div>
          </div>

          <div style={styles.annualCard}>
            <div style={styles.annualIconWrapper}>
              <FaBalanceScale color="#0080ff" size={20} />
            </div>
            <div style={styles.annualInfo}>
              <span style={styles.annualLabel}>{t("annual.monthlyAverage")}</span>
              <span style={{ ...styles.annualValue, color: "#0080ff" }}>
                {fmtCurrency(annualSummary.promedioMensual)}
              </span>
            </div>
          </div>

          <div style={styles.annualCard}>
            <div style={styles.annualIconWrapper}>
              <FaCalendarAlt color="#8b5cf6" size={20} />
            </div>
            <div style={styles.annualInfo}>
              <span style={styles.annualLabel}>{t("annual.monthsPaid")}</span>
              <span style={{ ...styles.annualValue, color: "#8b5cf6" }}>
                {t("annual.monthsPaidValue", { count: annualSummary.mesesPagados })}
              </span>
            </div>
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

// ============================================================
// Estilos
// ============================================================
const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "20px",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: {
    background: "white",
    borderRadius: "10px",
    padding: "16px 20px",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  headerIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    backgroundColor: "#0080ff",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: "20px",
    fontWeight: 400,
    margin: 0,
    color: "#0080ff",
  },
  subtitle: {
    fontSize: "13px",
    color: "#6b7280",
    margin: "2px 0 0 0",
    fontWeight: 400,
  },
  cardsRow: {
    display: "flex",
    gap: "10px",
  },
  card: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    gap: "12px",
    borderRadius: "10px",
    padding: "14px 16px",
    background: "white",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  iconWrapper: {
    backgroundColor: "#0080ff",
    borderRadius: "8px",
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardTitle: {
    fontSize: "13px",
    fontWeight: 400,
    margin: "0 0 4px 0",
    color: "#6b7280",
  },
  amount: {
    fontSize: "22px",
    fontWeight: 400,
    margin: 0,
    color: "#1a1a1a",
  },
  section: {
    background: "white",
    borderRadius: "10px",
    padding: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "14px",
  },
  sectionIconWrapper: {
    width: "32px",
    height: "32px",
    borderRadius: "7px",
    backgroundColor: "#e8f4ff",
    color: "#0080ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 400,
    margin: 0,
    color: "#1a1a1a",
    flex: 1,
  },
  sectionBadge: {
    fontSize: "11px",
    fontWeight: 400,
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
    padding: "4px 10px",
    borderRadius: "20px",
  },
  // Los estilos del gráfico y paneles están inline en el render
  annualGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "10px",
  },
  annualCard: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  annualIconWrapper: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    backgroundColor: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
  },
  annualInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  annualLabel: {
    fontSize: "11px",
    fontWeight: 400,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  annualValue: {
    fontSize: "18px",
    fontWeight: 400,
  },
  yearNav: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  yearNavBtn: {
    width: "28px",
    height: "28px",
    borderRadius: "6px",
    border: "1px solid #e5e7eb",
    backgroundColor: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    transition: "all 0.15s ease",
  },
  yearLabel: {
    fontSize: "14px",
    fontWeight: 400,
    color: "#374151",
    minWidth: "40px",
    textAlign: "center",
  },
};

export default Cuentas;
